import { inject } from "fw";
import { createFrom, createFromArray } from "fw-model";

import { AtsOrganization, AtsOrgnaizationIdName, AtsOrganizationPostArgs, AtsOrganizationPutArgs, EntitySelection } from "models/ats-organization";

import { ApplicationSourceSearchResult } from "models/ats-application-source";

import { AtsUser } from "models/ats-user";
import { OrganizationIndexingErrorInfo } from "models/ats-index-error";
import { Manager } from "./manager";
import { AtsExportTask, ExportTaskSearchResult } from "models/monitor-exports";
import { AtsImportRetry, ImportTaskSearchResult, AtsImportTaskExtended } from "models/monitor-application-imports";
import { AtsApplication } from "models/ats-application";
import { TaskRequest } from "models/task-request";
import { AtsAppMetrics, AtsOrgMetrics, AtsSystemMetrics } from "models/ats-metrics";
import { AtsSeasonAliasList } from "models/ats-season-alias-list";
import { ResponseResult } from "models/response-result";
import { AtsFileSourcePullHistorySearchResult } from "models/monitor-file-source-pull-histories";
import { FileSourceSearchResult } from "models/ats-file-source";
import { CountResult } from "models/aggregations";
import { AtsInterviewPortalExportHistorySearchResult, Season } from "models/monitor-interview-portal-export-histories";
import { AtsAutomationRuleTaskSearchResult, AtsAutomationRuleExecution } from "models/monitor-automation-rules";
import { AtsWorkflowRun, AtsWorkflowTaskSearchResult } from "models/monitor-workflows";
import { OrganizationAppPropertyErrorInfo } from "models/organization-app-property-error";
import { ApplicationPropertyErrorInfo } from "models/application-property-error-Info";
import { SearchResult } from "models/search-result";
import { AtsInterviewPortalImportsSearchResult } from "models/monitor-interview-portal-import-histories";
import { ApplicationImportTrackingSearchResult } from "models/monitor-application-import-tracking";
import { AtsTaskRequestSearchResult } from "models/monitor-task-requests";

const CHUNK_SIZE = 100;

@inject
export class AtsRepository {
    constructor(private s: Manager) {}

    async organizations(f = null, s = null, sort = null, page = 1, pageSize = 20): Promise<ResponseResult<AtsOrganization>> {
        let res;

        try {
            res = await this.s.get<AtsOrganization[]>("ats/organizations", { f, s, sort, page, pageSize });

            res = {
                list: createFromArray(AtsOrganization, res.body),
                total: parseInt(res.headers["x-manager-total"], 10)
            };
        } catch (e) {
            res = { list: [], total: 0 };
        }

        return res;
    }

    public async tag(
        id: string,
        data: {
            Selection: EntitySelection;
            AddTags: string[];
            RemoveTags: string[];
        }
    ): Promise<TaskRequest> {
        const res = await this.s.post(`ats/organizations/tag`, data);

        return createFrom(TaskRequest, res.body);
    }

    async applicationSources(organizationId = null, filter = null, search = null, sort = null, pageSize = 20, previousPageToken = null, nextPageToken = null) {
        const res = await this.s.get<ApplicationSourceSearchResult>("ats/application-source-list", {
            organizationId,
            filter,
            search,
            sort,
            pageSize,
            previousPageToken,
            nextPageToken
        });
        return createFrom(ApplicationSourceSearchResult, res.body);
    }

    public async fileSources(organizationId = null, filter = null, search = null, sort = null, pageSize = 20, previousPageToken = null, nextPageToken = null) {
        const res = await this.s.get<ApplicationSourceSearchResult>("ats/file-source-list", {
            organizationId,
            filter,
            search,
            sort,
            pageSize,
            previousPageToken,
            nextPageToken
        });
        return createFrom(FileSourceSearchResult, res.body);
    }

    // THIS IS TEMP, until I can properly call the above method :)
    async allOrganizations() {
        const res = await this.s.get<AtsOrganization[]>("ats/organizations");
        return createFromArray(AtsOrganization, res.body);
    }

    public async organizationByIdsInChunk(
        idsToBeLoaded: string[],
        fieldPaths?: (keyof AtsOrganization)[],
        chunkSize = CHUNK_SIZE
    ): Promise<AtsOrgnaizationIdName[]> {
        const promises: Promise<AtsOrganization[]>[] = [];
        for (let index = 0; index < idsToBeLoaded.length; index += chunkSize) {
            const chunk = idsToBeLoaded.slice(index, index + chunkSize);
            promises.push(this.organizationByIds(chunk, fieldPaths));
        }

        return (await Promise.all(promises)).flat();
    }

    async organizationByIds(ids: string[], fieldPaths?: (keyof AtsOrganization)[]): Promise<AtsOrganization[]> {
        const filter: string = `(${ids.map((id: string) => `id:${id}`).join(" OR ")})`;
        const res = await this.s.get<AtsOrganization[]>("ats/organizations", {
            f: filter,
            pageSize: ids.length,
            fieldPaths: fieldPaths?.join(",")
        });

        return createFromArray(AtsOrganization, res.body);
    }

    async organizationById(organizationId: string) {
        const res = await this.s.get<AtsOrganization>(`ats/organizations/${organizationId}`);
        return createFrom(AtsOrganization, res.body);
    }

    async postOrganization(args: AtsOrganizationPostArgs) {
        const res = await this.s.post<AtsOrganization>("ats/organizations", args);
        return createFrom(AtsOrganization, res.body);
    }

    async putOrganization(organizationId: string, args: AtsOrganizationPutArgs) {
        const res = await this.s.put<AtsOrganization>(`ats/organizations/${organizationId}`, args);
        return createFrom(AtsOrganization, res.body);
    }

    async users(f = null, s = null, sort = null, page = 1, pageSize = 20) {
        const res = await this.s.get<AtsUser[]>("ats/users", { f, s, sort, page, pageSize });
        return { list: createFromArray(AtsUser, res.body), total: parseInt(res.headers["x-manager-total"], 10) };
    }

    async userById(userId: string) {
        const res = await this.s.get<AtsUser>(`ats/users/${userId}`);
        return createFrom(AtsUser, res.body);
    }

    public async deactivateMfa(userId: string) {
        const path = `ats/users/${userId}/deactivate-mfa`;
        const res = await this.s.post<string>(path, null);
        return res.body;
    }

    async seasonConfigurationBySeasonId(seasonId: string) {
        const res = await this.s.get<any>(`ats/season/${seasonId}/configuration`);
        return res.body;
    }

    // returns the token
    public async atsLogin(organizationId: string, impersonationOrganizationUserId: string = null) {
        let path = `ats/user/login?organizationId=${organizationId}`;
        if (impersonationOrganizationUserId && impersonationOrganizationUserId.length > 0)
            path += `&impersonationOrganizationUserId=${impersonationOrganizationUserId}`;
        const res = await this.s.post<string>(path, null);
        return res.body;
    }

    public async atsExportsList(
        filter: string,
        search: string = null,
        orgFilter: string = null,
        sort: string = null,
        previousPageToken: string = null,
        nextPageToken: string = null,
        fieldsPaths: string = null,
        aggs: string = null,
        aggregationsOnly: boolean = false,
        pageSize: number = 20
    ): Promise<ExportTaskSearchResult> {
        const res = await this.s.get<ExportTaskSearchResult>("ats/exports", {
            filter,
            search,
            sort,
            pageSize,
            previousPageToken,
            nextPageToken,
            aggs,
            fieldsPaths,
            aggregationsOnly,
            orgFilter
        });
        return createFrom(ExportTaskSearchResult, res.body);
    }

    public async atsAutomationRulesList(
        filter: string,
        search: string = null,
        sort: string = null,
        previousPageToken: string = null,
        nextPageToken: string = null,
        fieldsPaths: string = null,
        aggs: string = null,
        aggregationsOnly: boolean = false,
        orgFilter: string = null,
        pageSize: number = 20
    ): Promise<AtsAutomationRuleTaskSearchResult> {
        const res = await this.s.get<AtsAutomationRuleTaskSearchResult>("ats/automation-rules", {
            filter,
            search,
            sort,
            pageSize,
            previousPageToken,
            nextPageToken,
            aggs,
            fieldsPaths,
            aggregationsOnly,
            orgFilter
        });
        return createFrom(AtsAutomationRuleTaskSearchResult, res.body);
    }
    public async atsAutomationRuleById(id: string): Promise<AtsAutomationRuleExecution> {
        const res = await this.s.get<AtsAutomationRuleExecution>(`ats/automation-rules/${id}`);
        return createFrom(AtsAutomationRuleExecution, res.body);
    }

    public async atsWorkflowsList(
        filter: string,
        search: string = null,
        sort: string = null,
        previousPageToken: string = null,
        nextPageToken: string = null,
        fieldsPaths: string = null,
        aggs: string = null,
        aggregationsOnly: boolean = false,
        pageSize: number = 20,
        orgFilter: string = null
    ): Promise<AtsWorkflowTaskSearchResult> {
        const res = await this.s.get<AtsWorkflowTaskSearchResult>("ats/workflows", {
            filter,
            search,
            sort,
            pageSize,
            previousPageToken,
            nextPageToken,
            aggs,
            fieldsPaths,
            aggregationsOnly,
            orgFilter
        });
        return createFrom(AtsWorkflowTaskSearchResult, res.body);
    }

    public async atsWorkflowById(id: string): Promise<AtsWorkflowRun> {
        const res = await this.s.get<AtsWorkflowRun>(`ats/workflows/${id}`);
        return createFrom(AtsWorkflowRun, res.body);
    }

    public async atsApplicationsList(
        filter: string,
        search: string = null,
        sort: string = null,
        previousPageToken: string = null,
        nextPageToken: string = null,
        fieldsPaths: string = null,
        aggs: string = null,
        aggregationsOnly: boolean = false,
        orgFilter: string = null,
        pageSize: number = 20
    ): Promise<ImportTaskSearchResult> {
        const res = await this.s.get<ImportTaskSearchResult>("ats/application-source-history", {
            filter,
            search,
            sort,
            pageSize,
            previousPageToken,
            nextPageToken,
            aggs,
            fieldsPaths,
            aggregationsOnly,
            orgFilter
        });
        return createFrom(ImportTaskSearchResult, res.body);
    }

    public async atsRetryImport(id: string): Promise<any> {
        const res = await this.s.post<any[]>(`ats/application-source-history/${id}/retry`, {});
        return createFrom(AtsImportRetry, res.body);
    }

    public async atsExtendedImportHistoryById(id: string): Promise<any> {
        const res = await this.s.get<any[]>(`ats/application-source-history/${id}/extended`, {});
        return createFrom(AtsImportTaskExtended, res.body);
    }

    public async atsFileSourceHistoriesList(
        filter: string,
        search: string = null,
        sort: string = null,
        previousPageToken: string = null,
        nextPageToken: string = null,
        fieldsPaths: string = null,
        aggs: string = null,
        aggregationsOnly: boolean = false,
        pageSize: number = 20
    ): Promise<AtsFileSourcePullHistorySearchResult> {
        const res = await this.s.get<AtsFileSourcePullHistorySearchResult>("ats/file-source-pull-history", {
            filter,
            search,
            sort,
            pageSize,
            previousPageToken,
            nextPageToken,
            aggs,
            fieldsPaths,
            aggregationsOnly
        });
        return createFrom(AtsFileSourcePullHistorySearchResult, res.body);
    }

    public async atsInterviewPortalImportsList(
        filter: string,
        search: string = null,
        sort: string = null,
        previousPageToken: string = null,
        nextPageToken: string = null,
        fieldsPaths: string = null,
        aggs: string = null,
        aggregationsOnly: boolean = false,
        orgFilter: string = null,
        pageSize: number = 20
    ): Promise<AtsInterviewPortalImportsSearchResult> {
        const res = await this.s.get<AtsInterviewPortalImportsSearchResult>("ats/interview-portal-import-history", {
            filter,
            search,
            sort,
            pageSize,
            previousPageToken,
            nextPageToken,
            aggs,
            fieldsPaths,
            aggregationsOnly,
            orgFilter
        });
        return createFrom(AtsInterviewPortalImportsSearchResult, res.body);
    }

    public async atsInterviewPortalExportHistoryList(
        filter: string,
        search: string = null,
        sort: string = null,
        previousPageToken: string = null,
        nextPageToken: string = null,
        fieldsPaths: string = null,
        aggs: string = null,
        aggregationsOnly: boolean = false,
        orgFilter: string = null,
        pageSize: number = 20
    ): Promise<AtsInterviewPortalExportHistorySearchResult> {
        const res = await this.s.get<AtsInterviewPortalExportHistorySearchResult>("ats/interview-portal-export-history", {
            filter,
            search,
            sort,
            pageSize,
            previousPageToken,
            nextPageToken,
            aggs,
            fieldsPaths,
            aggregationsOnly,
            orgFilter
        });

        return createFrom(AtsInterviewPortalExportHistorySearchResult, res.body);
    }

    public async atsExportRetryDelivery(id: string, retryDelivery: boolean = false): Promise<AtsExportTask> {
        const res = await this.s.put<AtsExportTask>(`ats/export-task/${id}`, null, { retryDelivery });
        return createFrom(AtsExportTask, res.body);
    }

    public async atsExportById(id: string): Promise<AtsExportTask> {
        const res = await this.s.get<AtsExportTask>(`ats/exports/${id}`);
        return createFrom(AtsExportTask, res.body);
    }

    public async searchFields(): Promise<string[]> {
        const res = await this.s.get<string[]>("ats/searchfields");
        return res.body;
    }

    public async atsApplicationCount(filter: string, aggs: string = null, orgFilter: string = null): Promise<CountResult> {
        const res = await this.s.get<CountResult>("ats/application-count", {
            filter,
            aggs,
            orgFilter
        });

        return createFrom(CountResult, res.body);
    }

    public async getSeasonById(id: string): Promise<Season> {
        const res = await this.s.get<Season>(`ats/season/${id}`);
        return createFrom(Season, res.body);
    }

    public async seasonAliasList(organizationId: string, seasonId: string): Promise<AtsSeasonAliasList> {
        const res = await this.s.get<AtsSeasonAliasList>(`ats/seasonaliaslist`, { organizationId, seasonId });
        return res.body;
    }

    public async indexErrorTotals(size) {
        const res = await this.s.get<any[]>("ats/index-error/totals", { size });
        return createFromArray(OrganizationIndexingErrorInfo, res.body);
    }

    public async organizationAppPropertyErrorTotals(size) {
        const res = await this.s.get<any[]>("ats/organization-app-property-error/totals", { size });
        return createFromArray(OrganizationAppPropertyErrorInfo, res.body);
    }

    public async organizationAppPropertyErrorSearch(organizationId: string, pageSize: number, previousPageToken: string, nextPageToken: string) {
        const res = await this.s.get<SearchResult<ApplicationPropertyErrorInfo>>("ats/organization-app-property-error/search", {
            organizationId,
            pageSize,
            previousPageToken,
            nextPageToken
        });
        return {
            list: createFromArray(ApplicationPropertyErrorInfo, res.body.Results),
            previousPageToken: res.body.PreviousPageToken,
            nextPageToken: res.body.NextPageToken,
            total: res.body.Total
        };
    }

    public async indexErrorSearch(organizationId: string, seasonId: string, pageSize: number, previousPageToken: string, nextPageToken: string) {
        const res = await this.s.get<AtsApplication[]>("ats/index-error/search", {
            organizationId,
            seasonId,
            pageSize,
            previousPageToken,
            nextPageToken
        });
        return {
            list: createFromArray(AtsApplication, res.body),
            previousPageToken: res.headers["x-manager-previous-page-token"],
            nextPageToken: res.headers["x-manager-next-page-token"],
            total: parseInt(res.headers["x-manager-total"], 10)
        };
    }

    public async reindex(organizationId: string, deleteFirst: boolean, filter: string = null) {
        await this.s.get("ats/application/reindex", { organizationId, delete: deleteFirst, f: filter });
    }

    public async atsTaskRequestsList(
        filter: string,
        search: string = null,
        sort: string = null,
        previousPageToken: string = null,
        nextPageToken: string = null,
        fieldsPaths: string = null,
        aggs: string = null,
        aggregationsOnly: boolean = false,
        pageSize: number = 20,
        orgFilter: string = null
    ): Promise<AtsTaskRequestSearchResult> {
        const res = await this.s.get<AtsTaskRequestSearchResult>("ats/task-requests/list", {
            filter,
            search,
            sort,
            pageSize,
            previousPageToken,
            nextPageToken,
            aggs,
            fieldsPaths,
            aggregationsOnly,
            orgFilter
        });
        return createFrom(AtsTaskRequestSearchResult, res.body);
    }

    public async appMetrics(organizationId: string = null) {
        const response = organizationId
            ? await this.s.get<AtsAppMetrics>(`ats/organizations/${organizationId}/metrics/app`)
            : await this.s.get<AtsAppMetrics>("ats/metrics/app");
        return createFrom(AtsAppMetrics, response.body);
    }

    public async orgMetrics(organizationId: string) {
        const response = await this.s.get<AtsOrgMetrics>(`ats/organizations/${organizationId}/metrics`);
        return createFrom(AtsOrgMetrics, response.body);
    }

    public async systemMetrics() {
        const response = await this.s.get<AtsSystemMetrics>("ats/metrics/system");
        return createFrom(AtsSystemMetrics, response.body);
    }

    public async atsApplicationImportTrackingList(
        filter: string,
        search: string = null,
        sort: string = null,
        previousPageToken: string = null,
        nextPageToken: string = null,
        fieldsPaths: string = null,
        aggs: string = null,
        aggregationsOnly: boolean = false,
        orgFilter: string = null,
        pageSize: number = 20
    ): Promise<ApplicationImportTrackingSearchResult> {
        const res = await this.s.get<ApplicationImportTrackingSearchResult>("ats/application-import-tracking", {
            filter,
            search,
            sort,
            pageSize,
            previousPageToken,
            nextPageToken,
            aggs,
            fieldsPaths,
            aggregationsOnly,
            orgFilter
        });

        return createFrom(ApplicationImportTrackingSearchResult, res.body);
    }

    public async appSyncEnable(organizationId: string, casApplicationId: number, applicationId: string | null): Promise<void> {
        const params = new URLSearchParams();
        params.append("atsOrganizationId", organizationId);
        if (applicationId) {
            params.append("atsApplicationId", applicationId);
        }

        await this.s.post(`ats/application-import-tracking/app-sync/${casApplicationId}/enable?${params.toString()}`, null);
    }

    public async appSyncDisable(organizationId: string, casApplicationId: number, applicationId: string | null): Promise<void> {
        const params = new URLSearchParams();
        params.append("atsOrganizationId", organizationId);
        if (applicationId) {
            params.append("atsApplicationId", applicationId);
        }

        await this.s.post(`ats/application-import-tracking/app-sync/${casApplicationId}/disable?${params.toString()}`, null);
    }
}
